import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Film, ListVideo, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { PageHeading } from '@/app/PageHeading';
import { Alert } from '@/components/arc/alert/alert';
import { Button } from '@/components/arc/button/button';
import { Dialog, DialogClose, DialogContent } from '@/components/arc/dialog/dialog';
import { Drawer, DrawerContent } from '@/components/arc/drawer/drawer';
import { DropdownMenu } from '@/components/arc/dropdown-menu/dropdown-menu';
import { EmptyState } from '@/components/arc/empty-state/empty-state';
import { Input } from '@/components/arc/input/input';
import { Progress } from '@/components/arc/progress/progress';
import { RadioGroup } from '@/components/arc/radio-group/radio-group';
import { Select } from '@/components/arc/select/select';
import { Skeleton } from '@/components/arc/skeleton/skeleton';
import { SortableDataTable, type DataColumn } from '@/components/arc/sortable-data-table/sortable-data-table';
import { Textarea } from '@/components/arc/textarea/textarea';
import { useToastStack } from '@/components/arc/toast-stack/toast-stack';
import { SourceStatusBadge, SourceTypeBadge } from '@/components/status/StatusBadge';
import {
  useBulkAnalyze,
  useCreateSource,
  useDeleteSource,
  useDiscoverSource,
  useSourceEstimate,
  useSources,
  useVideoStatusCount,
} from '@/api/hooks';
import { errorMessage } from '@/api/client';
import type { AnalysisTier, SourceCreate, SourceOut, SourceType, UploadOut } from '@/api/types';
import { SOURCE_TYPES } from '@/api/types';
import { useApi, useTimezone } from '@/module/MomentProvider';
import { useHref } from '@/module/basePath';
import { fmtDate, fmtDurationHuman, fmtMoney, fmtNumber } from '@/lib/format';
import { FPS_OPTIONS, TIER_OPTIONS, fpsValue } from '@/pages/videos/AnalyzeDialog';
import styles from './SourcesPage.module.css';

type SourceRow = { [K in keyof SourceOut]: SourceOut[K] };

/** A bulk analysis started from the dialog: what was queued and where the source stood at that moment. */
interface AnalysisRun {
  sourceId: string;
  total: number;
  analyzedBefore: number;
  startedAt: number;
}

/** Videos queued or analyzing for a source; older API builds omit the field. */
const activeCount = (s: SourceOut) => s.active_count ?? 0;

const TYPE_LABEL: Record<SourceType, string> = {
  youtube_video: 'YouTube video',
  youtube_channel: 'YouTube channel',
  youtube_playlist: 'YouTube playlist',
  youtube_search: 'YouTube search',
  url_list: 'URL list',
  publisher_feed: 'Publisher feed',
  ctv_catalog: 'CTV catalog',
};

const TYPE_HELP: Record<SourceType, { label: string; placeholder: string; hint: string }> = {
  youtube_video: {
    label: 'YouTube video URL',
    placeholder: 'https://www.youtube.com/watch?v=',
    hint: 'One video.',
  },
  youtube_channel: {
    label: 'YouTube channel',
    placeholder: '@handle, UC id or channel URL',
    hint: 'Uploads are discovered through the YouTube Data API.',
  },
  youtube_playlist: {
    label: 'YouTube playlist',
    placeholder: 'PL id or playlist URL',
    hint: 'Every video in the playlist.',
  },
  youtube_search: {
    label: 'Search query',
    placeholder: 'kahvaltı tarifi, aile, ev',
    hint: 'Keywords from the brief. Finds matching videos through the YouTube Data API for the chosen region and language.',
  },
  url_list: {
    label: 'Video URLs',
    placeholder: 'One URL per line',
    hint: 'YouTube links, or pages that expose og:video or a JSON-LD VideoObject.',
  },
  publisher_feed: {
    label: 'Feed URL',
    placeholder: 'https://publisher.com/feed.rss',
    hint: 'MRSS or JSON feed.',
  },
  ctv_catalog: {
    label: 'CSV URL',
    placeholder: 'https://example.com/catalog.csv',
    hint: 'Columns: title, series, season, episode, genre, synopsis, trailer_url. Or paste the CSV below.',
  },
};

export function SourcesPage() {
  const navigate = useNavigate();
  const href = useHref();
  const tz = useTimezone();
  const { toast } = useToastStack();
  const sources = useSources();
  const create = useCreateSource();
  const discover = useDiscoverSource();
  const del = useDeleteSource();
  const estimate = useSourceEstimate();
  const bulk = useBulkAnalyze();
  const [addOpen, setAddOpen] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [deleting, setDeleting] = useState<SourceOut | null>(null);
  const [analyzing, setAnalyzing] = useState<SourceOut | null>(null);
  const [run, setRun] = useState<AnalysisRun | null>(null);

  const rows = useMemo<SourceRow[]>(() => sources.data?.pages.flatMap((p) => p.items) ?? [], [sources.data]);

  // Live progress for the dialog: the sources poll gives the done count, two status counts give queued and running.
  const liveSource = run ? rows.find((s) => s.id === run.sourceId) : undefined;
  const queuedCount = useVideoStatusCount(run?.sourceId, 'queued', !!run);
  const runningCount = useVideoStatusCount(run?.sourceId, 'analyzing', !!run);
  const progress = useMemo(() => {
    if (!run) return null;
    // Counts fetched before the run started (a previous run on the same source) must not end it early.
    const fresh = (at: number) => at >= run.startedAt;
    const queued = fresh(queuedCount.dataUpdatedAt) ? queuedCount.data : undefined;
    const running = fresh(runningCount.dataUpdatedAt) ? runningCount.data : undefined;
    const done = Math.max(0, (liveSource?.analyzed_count ?? run.analyzedBefore) - run.analyzedBefore);
    // Whatever left the queue without landing in done either failed or was skipped by the model.
    const failed = queued === undefined || running === undefined ? 0 : Math.max(0, run.total - queued - running - done);
    const finished =
      run.total === 0 || (queued === 0 && running === 0 && (!liveSource || activeCount(liveSource) === 0));
    return {
      queued: queued ?? run.total,
      running: running ?? 0,
      done,
      failed,
      pct: run.total === 0 ? 100 : Math.round((Math.min(done + failed, run.total) / run.total) * 100),
      finished,
    };
  }, [run, liveSource, queuedCount.data, queuedCount.dataUpdatedAt, runningCount.data, runningCount.dataUpdatedAt]);
  const runActive = !!run && !progress?.finished;

  // The sources list only polls on its own once the API reports active_count; refetch it here as well so the
  // done counter moves on older API builds too.
  const refetchSources = sources.refetch;
  useEffect(() => {
    if (!runActive) return;
    const id = setInterval(() => void refetchSources(), 5000);
    return () => clearInterval(id);
  }, [runActive, refetchSources]);

  const openAnalyze = (s: SourceOut) => {
    setAnalyzing(s);
    setRun(null);
    estimate.mutate({ id: s.id, tier: analysisTier(s), fps: analysisFps(s), only_unanalyzed: true });
  };
  const closeAnalyze = () => {
    setAnalyzing(null);
    setRun(null);
    estimate.reset();
    bulk.reset();
  };

  const runDiscover = async (s: SourceOut) => {
    try {
      await discover.mutateAsync(s.id);
      toast({ type: 'success', title: 'Discovery queued', description: s.title || s.external_id });
    } catch (e) {
      toast({ type: 'error', title: 'Could not start discovery', description: errorMessage(e) });
    }
  };

  const confirmAnalyze = async () => {
    if (!analyzing) return;
    try {
      const r = await bulk.mutateAsync({
        source_id: analyzing.id,
        tier: analysisTier(analyzing),
        fps: analysisFps(analyzing),
        only_unanalyzed: true,
      });
      // The dialog stays open and turns into a progress view; the counters come from the polls above.
      setRun({
        sourceId: analyzing.id,
        total: r.queued,
        analyzedBefore: analyzing.analyzed_count,
        startedAt: Date.now(),
      });
    } catch {
      /* shown inline in the dialog */
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await del.mutateAsync(deleting.id);
      setDeleting(null);
    } catch {
      /* shown inline in the dialog */
    }
  };

  const columns: DataColumn<SourceRow>[] = [
    {
      key: 'title',
      label: 'Source',
      render: (_v, s) => (
        <div className={styles.sourceCell}>
          <span className={styles.thumb} aria-hidden="true">
            {s.thumbnail_url && <img src={s.thumbnail_url} alt="" loading="lazy" />}
          </span>
          <span className={styles.sourceText}>
            <Link
              to={href(`/videos?source=${s.id}`)}
              className={styles.sourceLink}
              title={s.title || s.external_id}
            >
              {s.title || s.external_id}
            </Link>
            <span className={`${styles.sub} mono`}>{s.external_id}</span>
          </span>
        </div>
      ),
    },
    { key: 'type', label: 'Type', width: 124, render: (_v, s) => <SourceTypeBadge type={s.type} /> },
    {
      key: 'status',
      label: 'Status',
      width: 108,
      render: (_v, s) => (
        <span className={styles.statusCell}>
          <SourceStatusBadge status={s.status} />
          {s.error && (
            <span className={styles.error} title={s.error}>
              {s.error}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'video_count',
      label: 'Analyzed',
      width: 92,
      numeric: true,
      render: (_v, s) => `${fmtNumber(s.analyzed_count)} / ${fmtNumber(s.video_count)}`,
    },
    {
      key: 'total_duration_sec',
      label: 'Duration',
      width: 88,
      numeric: true,
      render: (v) => fmtDurationHuman(v as number),
    },
    {
      key: 'estimated_cost_usd',
      label: 'Est. cost',
      width: 92,
      numeric: true,
      render: (v) => fmtMoney(v as number, 2),
    },
    { key: 'created_at', label: 'Created', width: 112, render: (v) => fmtDate(v as string, tz) },
    {
      key: 'actions',
      label: 'Actions',
      width: 196,
      sortable: false,
      render: (_v, s) => (
        <span className={styles.actions}>
          <Button
            size="sm"
            variant="secondary"
            disabled={activeCount(s) > 0 || s.video_count === 0 || s.analyzed_count >= s.video_count}
            onClick={() => openAnalyze(s)}
          >
            {activeCount(s) > 0 ? `Running (${fmtNumber(activeCount(s))})` : 'Analyze all'}
          </Button>
          <DropdownMenu
            label="More"
            items={[
              {
                label: 'Discover again',
                icon: <RefreshCw size={16} strokeWidth={1.75} />,
                onSelect: () => void runDiscover(s),
              },
              {
                label: 'Open videos',
                icon: <Film size={16} strokeWidth={1.75} />,
                onSelect: () => navigate(href(`/videos?source=${s.id}`)),
              },
              {
                label: 'Delete source',
                icon: <Trash2 size={16} strokeWidth={1.75} />,
                destructive: true,
                separatorBefore: true,
                onSelect: () => setDeleting(s),
              },
            ]}
          />
        </span>
      ),
    },
  ];

  const est = estimate.data;

  return (
    <main className="page">
      <PageHeading
        title="Sources"
        description="YouTube channels, playlists, URL lists, publisher feeds and CTV catalogs that feed the video library."
        actions={
          <Button
            onClick={() => {
              setFormKey((k) => k + 1);
              setAddOpen(true);
            }}
          >
            <Plus size={16} strokeWidth={1.75} aria-hidden="true" />
            Add source
          </Button>
        }
      />

      {sources.isError ? (
        <Alert tone="danger" title="Could not load sources">
          <span className={styles.alertBody}>
            {errorMessage(sources.error)}
            <Button size="sm" variant="secondary" onClick={() => void sources.refetch()}>
              Try again
            </Button>
          </span>
        </Alert>
      ) : sources.isLoading ? (
        <Skeleton lines={6} label="Loading sources" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<ListVideo size={24} strokeWidth={1.5} />}
          title="No sources yet"
          description="Add a YouTube channel, playlist, URL list or feed to start discovering videos."
          action={
            <Button variant="secondary" onClick={() => setAddOpen(true)}>
              Add source
            </Button>
          }
        />
      ) : (
        <div className="stack-4">
          <div className="table-scroll">
            <SortableDataTable
              rows={rows}
              columns={columns}
              rowKey="id"
              caption="Sources"
              defaultSort={{ key: 'created_at', direction: 'desc' }}
            />
          </div>
          {sources.hasNextPage && (
            <div className={styles.loadMore}>
              <Button
                variant="secondary"
                loading={sources.isFetchingNextPage}
                onClick={() => void sources.fetchNextPage()}
              >
                Load more sources
              </Button>
            </div>
          )}
        </div>
      )}

      <Drawer open={addOpen} onOpenChange={setAddOpen}>
        <DrawerContent
          title="Add source"
          description="Discovery starts as soon as the source is saved."
          side="right"
        >
          <AddSourceForm
            key={formKey}
            loading={create.isPending}
            error={create.isError ? errorMessage(create.error) : null}
            onSubmit={async (body) => {
              try {
                const created = await create.mutateAsync(body);
                setAddOpen(false);
                toast({
                  type: 'success',
                  title: 'Source added, discovery started',
                  description: created.title || created.external_id,
                });
              } catch {
                /* shown inline in the form */
              }
            }}
          />
        </DrawerContent>
      </Drawer>

      <Dialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <DialogContent
          title="Delete source?"
          description="Videos stay in the library but lose their link to this source."
        >
          {deleting && (
            <p className={styles.dialogText}>
              <strong>{deleting.title || deleting.external_id}</strong> with {fmtNumber(deleting.video_count)}{' '}
              discovered {deleting.video_count === 1 ? 'video' : 'videos'}.
            </p>
          )}
          {del.isError && (
            <Alert tone="danger" title="Could not delete the source">
              {errorMessage(del.error)}
            </Alert>
          )}
          <div className={styles.dialogActions}>
            <DialogClose asChild>
              <Button type="button" variant="secondary">
                Cancel
              </Button>
            </DialogClose>
            <Button variant="danger" loading={del.isPending} onClick={() => void confirmDelete()}>
              Delete source
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!analyzing}
        onOpenChange={(open) => {
          // While videos are being analyzed the Close button is the only way out.
          if (!open && !runActive) closeAnalyze();
        }}
      >
        <DialogContent
          title={
            run ? (progress?.finished ? 'Analysis finished' : 'Analyzing videos') : 'Analyze all videos'
          }
          description={analyzing ? analyzing.title || analyzing.external_id : undefined}
          onEscapeKeyDown={(e) => runActive && e.preventDefault()}
          onPointerDownOutside={(e) => runActive && e.preventDefault()}
          onInteractOutside={(e) => runActive && e.preventDefault()}
        >
          {estimate.isPending ? (
            <Skeleton lines={4} label="Estimating cost" />
          ) : estimate.isError ? (
            <Alert tone="danger" title="Could not estimate the cost">
              <span className={styles.alertBody}>
                {errorMessage(estimate.error)}
                <Button size="sm" variant="secondary" onClick={() => analyzing && openAnalyze(analyzing)}>
                  Try again
                </Button>
              </span>
            </Alert>
          ) : est && analyzing ? (
            <dl className={styles.estimate}>
              <div>
                <dt>Videos to analyze</dt>
                <dd>{fmtNumber(est.video_count)}</dd>
              </div>
              <div>
                <dt>Total duration</dt>
                <dd>{fmtDurationHuman(est.total_duration_sec)}</dd>
              </div>
              <div>
                <dt>Model</dt>
                <dd className="mono">{est.model}</dd>
              </div>
              <div>
                <dt>Frame rate</dt>
                <dd>{analysisFps(analyzing) ? `${analysisFps(analyzing)} fps` : 'Auto'}</dd>
              </div>
              <div className={styles.estimateTotal}>
                <dt>Estimated cost</dt>
                <dd className="mono">{fmtMoney(est.estimated_cost_usd, 2)}</dd>
              </div>
            </dl>
          ) : null}
          {est && (
            <p className={styles.note}>
              The estimate uses list pricing and average tokens per video second. The actual cost is recorded
              per video after analysis.
            </p>
          )}
          {bulk.isError && (
            <Alert tone="danger" title="Could not queue the analysis">
              {errorMessage(bulk.error)}
            </Alert>
          )}
          {run && progress && (
            <section className={styles.run} aria-label="Analysis progress" aria-live="polite">
              <Progress
                value={progress.pct}
                label={
                  progress.finished
                    ? `${fmtNumber(progress.done)} of ${fmtNumber(run.total)} analyzed`
                    : `${fmtNumber(run.total)} ${run.total === 1 ? 'video' : 'videos'} queued`
                }
                showValue
              />
              <dl className={styles.counters}>
                <Counter label="Queued" value={progress.queued} />
                <Counter label="Running" value={progress.running} />
                <Counter label="Done" value={progress.done} />
                <Counter label="Failed or skipped" value={progress.failed} />
              </dl>
              <p className={styles.note}>
                {progress.finished
                  ? 'Skipped videos are ones the model declined to analyze. Their reason is on the video page.'
                  : 'Counts refresh every 5 seconds. Closing this dialog does not stop the analysis.'}
              </p>
            </section>
          )}
          <div className={styles.dialogActions}>
            {run ? (
              <Button
                type="button"
                variant={progress?.finished ? 'primary' : 'secondary'}
                onClick={closeAnalyze}
              >
                Close
              </Button>
            ) : (
              <>
                <DialogClose asChild>
                  <Button type="button" variant="secondary">
                    Cancel
                  </Button>
                </DialogClose>
                <Button
                  loading={bulk.isPending}
                  disabled={!est || est.video_count === 0}
                  onClick={() => void confirmAnalyze()}
                >
                  Analyze {est ? fmtNumber(est.video_count) : ''} videos
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </main>
  );
}

function Counter({ label, value }: { label: ReactNode; value: number }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd className="mono">{fmtNumber(value)}</dd>
    </div>
  );
}

function analysisTier(s: SourceOut): AnalysisTier {
  const t = s.config.tier;
  return t === 'premium' ? 'premium' : 'standard';
}
function analysisFps(s: SourceOut): number | null {
  const f = s.config.fps;
  return typeof f === 'number' && f > 0 ? f : null;
}

/** The form offers one type the API does not have: files are uploaded first, then saved as a url_list source. */
type FormType = SourceType | 'upload';
const UPLOAD_HELP = {
  label: 'Video files',
  hint: 'mp4, mov, webm or mkv. Each file becomes one video; analysis works like any other source.',
};
const UPLOAD_ACCEPT = '.mp4,.mov,.webm,.mkv,video/mp4,video/quicktime,video/webm,video/x-matroska';
const UPLOAD_EXT = /\.(mp4|mov|webm|mkv)$/i;

interface PendingFile {
  id: number;
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'done' | 'error';
  result?: UploadOut;
  error?: string;
}
let fileSeq = 0;
const fmtMb = (bytes: number) => `${(bytes / 1048576).toFixed(1)} MB`;

function AddSourceForm({
  onSubmit,
  loading,
  error,
}: {
  onSubmit: (body: SourceCreate) => void | Promise<void>;
  loading: boolean;
  error: string | null;
}) {
  const api = useApi();
  const [type, setType] = useState<FormType>('youtube_channel');
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [ext, setExt] = useState('');
  const [csv, setCsv] = useState('');
  const [title, setTitle] = useState('');
  const [fps, setFps] = useState('auto');
  const [tier, setTier] = useState<AnalysisTier>('standard');
  const [maxVideos, setMaxVideos] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [lang, setLang] = useState('');
  const [adapter, setAdapter] = useState('mrss');
  const [region, setRegion] = useState('TR');
  const [order, setOrder] = useState<'relevance' | 'viewCount' | 'date'>('relevance');
  const [videoDuration, setVideoDuration] = useState<'any' | 'short' | 'medium' | 'long'>('any');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const help = type === 'upload' ? TYPE_HELP.url_list : TYPE_HELP[type];
  const busy = loading || uploading;

  useEffect(() => setFieldError(null), [type, ext, csv]);

  const patchFile = (id: number, patch: Partial<PendingFile>) =>
    setFiles((fs) => fs.map((f) => (f.id === id ? { ...f, ...patch } : f)));

  const pickFiles = (picked: FileList | null) => {
    if (!picked) return;
    const rejected: string[] = [];
    const added: PendingFile[] = [];
    for (const file of Array.from(picked)) {
      if (!UPLOAD_EXT.test(file.name)) {
        rejected.push(file.name);
        continue;
      }
      added.push({ id: ++fileSeq, file, progress: 0, status: 'pending' });
    }
    setFiles((fs) => [
      ...fs,
      ...added.filter((a) => !fs.some((f) => f.file.name === a.file.name && f.file.size === a.file.size)),
    ]);
    setFieldError(rejected.length ? `Not a supported video file: ${rejected.join(', ')}` : null);
  };

  const baseConfig = (): NonNullable<SourceCreate['config']> => ({
    fps: fpsValue(fps),
    tier,
    max_videos: maxVideos ? Number(maxVideos) : null,
    date_from: dateFrom ? new Date(dateFrom).toISOString() : null,
    language: lang.trim() || null,
  });

  // Upload every file that is not stored yet (one at a time, with progress), then save one url_list source.
  const submitUpload = async () => {
    if (files.length === 0) {
      setFieldError('Choose at least one video file.');
      return;
    }
    setUploading(true);
    try {
      const results: UploadOut[] = [];
      for (const f of files) {
        if (f.status === 'done' && f.result) {
          results.push(f.result);
          continue;
        }
        patchFile(f.id, { status: 'uploading', progress: 0, error: undefined });
        try {
          const result = await api.uploads.create(f.file, (p) => patchFile(f.id, { progress: p }));
          patchFile(f.id, { status: 'done', progress: 1, result });
          results.push(result);
        } catch (err) {
          patchFile(f.id, { status: 'error', error: errorMessage(err) });
          setFieldError('A file could not be uploaded. Remove it or try again; uploaded files are kept.');
          return;
        }
      }
      const config = baseConfig();
      config.urls = results.map((r) => r.url);
      config.upload_keys = results.map((r) => r.key);
      const first = results[0]?.filename.replace(/\.[^.]+$/, '') ?? null;
      await onSubmit({ type: 'url_list', external_id: 'upload', title: title.trim() || first, config });
    } finally {
      setUploading(false);
    }
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (type === 'upload') {
      void submitUpload();
      return;
    }
    const id = ext.trim();
    const inlineCsv = type === 'ctv_catalog' && csv.trim().length > 0;
    if (!id && !inlineCsv) {
      setFieldError(
        type === 'ctv_catalog'
          ? 'Enter a CSV URL or paste the CSV text below.'
          : `Enter the ${help.label.toLowerCase()}.`,
      );
      return;
    }
    const config = baseConfig();
    if (type === 'url_list')
      config.urls = ext
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
    if (type === 'publisher_feed') config.feed_adapter = adapter;
    if (type === 'youtube_search') {
      config.region = region.trim().toUpperCase() || 'TR';
      config.relevance_language = lang.trim().toLowerCase() || 'tr';
      config.order = order;
      config.video_duration = videoDuration;
      if (!config.max_videos) config.max_videos = 100;
    }
    if (inlineCsv) config.csv_text = csv;
    void onSubmit({ type, external_id: id || 'inline', title: title.trim() || null, config });
  };

  return (
    <form onSubmit={submit} className={styles.form}>
      <Select
        label="Type"
        options={[
          ...SOURCE_TYPES.map((t) => ({ value: t as string, label: TYPE_LABEL[t] })),
          { value: 'upload', label: 'Upload files' },
        ]}
        value={type}
        onValueChange={(v) => setType(v as FormType)}
      />
      {type === 'upload' ? (
        <div className={styles.fileField}>
          <label className={styles.fileLabel} htmlFor="add-source-files">
            {UPLOAD_HELP.label}
          </label>
          <input
            id="add-source-files"
            className={styles.fileInput}
            type="file"
            multiple
            accept={UPLOAD_ACCEPT}
            disabled={busy}
            onChange={(e) => {
              pickFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <p className={styles.fileHint}>{UPLOAD_HELP.hint}</p>
          {fieldError && (
            <p className={styles.fileError} role="alert">
              {fieldError}
            </p>
          )}
          {files.length > 0 && (
            <ul className={styles.fileList}>
              {files.map((f) => (
                <li key={f.id} className={styles.fileRow}>
                  <Progress
                    value={f.status === 'done' ? 100 : Math.round(f.progress * 100)}
                    label={f.file.name}
                    showValue={f.status !== 'pending'}
                  />
                  <div className={styles.fileMeta}>
                    {f.status === 'error' ? (
                      <span className={styles.fileProblem}>{f.error}</span>
                    ) : (
                      <span>
                        {fmtMb(f.file.size)}
                        {f.status === 'uploading' ? ', uploading' : f.status === 'done' ? ', uploaded' : ''}
                      </span>
                    )}
                    {!busy && (
                      <button
                        type="button"
                        className={styles.fileRemove}
                        onClick={() => setFiles((fs) => fs.filter((x) => x.id !== f.id))}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : type === 'url_list' ? (
        <Textarea
          label={help.label}
          rows={5}
          value={ext}
          onChange={(e) => setExt(e.target.value)}
          placeholder={help.placeholder}
          description={help.hint}
          error={fieldError ?? undefined}
        />
      ) : (
        <Input
          label={help.label}
          value={ext}
          onChange={(e) => setExt(e.target.value)}
          placeholder={help.placeholder}
          description={help.hint}
          error={fieldError ?? undefined}
        />
      )}
      {type === 'ctv_catalog' && (
        <Textarea
          label="CSV text"
          rows={5}
          value={csv}
          onChange={(e) => setCsv(e.target.value)}
          placeholder="title,series,season,episode,genre,synopsis,trailer_url"
          description="Pasted CSV replaces the URL above."
          className="mono"
        />
      )}
      {type === 'youtube_search' && (
        <div className={styles.formRow}>
          <Input
            label="Region"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            placeholder="TR"
            description="ISO country code. Results are ranked for this market."
            maxLength={2}
          />
          <Select
            label="Sort by"
            options={[
              { value: 'relevance', label: 'Relevance' },
              { value: 'viewCount', label: 'View count' },
              { value: 'date', label: 'Newest' },
            ]}
            value={order}
            onValueChange={(v) => setOrder(v as 'relevance' | 'viewCount' | 'date')}
          />
          <Select
            label="Length"
            options={[
              { value: 'any', label: 'Any length' },
              { value: 'short', label: 'Under 4 minutes' },
              { value: 'medium', label: '4 to 20 minutes' },
              { value: 'long', label: 'Over 20 minutes' },
            ]}
            value={videoDuration}
            onValueChange={(v) => setVideoDuration(v as 'any' | 'short' | 'medium' | 'long')}
          />
        </div>
      )}
      {type === 'publisher_feed' && (
        <Select
          label="Feed adapter"
          options={[
            { value: 'mrss', label: 'MRSS' },
            { value: 'json', label: 'JSON' },
          ]}
          value={adapter}
          onValueChange={setAdapter}
        />
      )}
      <Input
        label="Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        description={type === 'upload' ? 'Optional. Defaults to the first file name.' : 'Optional. Defaults to the discovered name.'}
      />
      <RadioGroup
        label="Tier"
        options={TIER_OPTIONS}
        value={tier}
        onValueChange={(v) => setTier(v as AnalysisTier)}
      />
      <Select label="Frame rate" options={FPS_OPTIONS} value={fps} onValueChange={setFps} />
      {type !== 'upload' && (
      <div className={styles.formRow}>
        <Input
          label="Max videos"
          type="number"
          min={1}
          inputMode="numeric"
          value={maxVideos}
          onChange={(e) => setMaxVideos(e.target.value)}
          description={
            type === 'youtube_search'
              ? 'Defaults to 100. Each 50 results cost 100 YouTube API units.'
              : 'Leave empty to discover every video.'
          }
        />
        <Input
          label="Published after"
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          description="Optional."
        />
      </div>
      )}
      <Input
        label="Language hint"
        value={lang}
        onChange={(e) => setLang(e.target.value)}
        placeholder="tr"
        description="ISO 639-1 code. Helps transcription when the spoken language is known."
      />
      {error && (
        <Alert tone="danger" title="Could not add the source">
          {error}
        </Alert>
      )}
      <div className={styles.dialogActions}>
        <Button type="submit" loading={busy}>
          {type === 'upload' && files.length > 0
            ? `Upload ${files.length} ${files.length === 1 ? 'file' : 'files'} and add source`
            : 'Add source'}
        </Button>
      </div>
    </form>
  );
}
